import { Minimize2 } from "lucide-react";
import { CSSProperties, useMemo } from "react";
import { useNavigate } from "react-router";

import { runViewTransition } from "../../../../run-view-transition";
import { ScrollShadow } from "../../../../scroll-shadow";
import { SkeletonBlock } from "../../../../skeleton-block";
import { useDocumentTitle } from "../../../../use-document-title";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./highest-failure-rate.module.css";

/**
 * Full-page `/qa-automation/highest-failure-rate` route: the "maximised"
 * ranked-list view of the QA automation dashboard's "Tests with the highest
 * failure rate" card (`../recent-runs.tsx`'s `HIGHEST_FAILURE_RATE` +
 * `RankedCard`). Deliberately NOT importing from that file (it's owned by a
 * different in-progress edit) — the `RankedRow` type and the 5 hand-authored
 * rows are copied verbatim below, then extended with deterministic synthetic
 * rows for infinite scroll, same pattern as
 * `../../workflows/pages/runs.tsx`. All data is static mock content — there
 * is no live QA backend in `platform` yet.
 */

type RankedRow = {
  primary: string;
  context: string;
  primaryValue: string;
  secondaryValue: string;
  /** 0..1, how far the inline bar fills. */
  barFraction: number;
  barTone: "duration" | "fail";
};

const HIGHEST_FAILURE_RATE: RankedRow[] = [
  {
    primary: "SSO login — Okta redirect after session timeout",
    context: "web › auth › sso",
    primaryValue: "42%",
    secondaryValue: "8/19 runs",
    barFraction: 0.42,
    barTone: "fail",
  },
  {
    primary: "Deal pipeline — drag card across virtualized columns",
    context: "desktop › pipeline › board",
    primaryValue: "31%",
    secondaryValue: "5/16 runs",
    barFraction: 0.31,
    barTone: "fail",
  },
  {
    primary: "Portfolio dashboard — live price ticker reconnect",
    context: "desktop › portfolio › ticker",
    primaryValue: "24%",
    secondaryValue: "4/17 runs",
    barFraction: 0.24,
    barTone: "fail",
  },
  {
    primary: "Fund report — chart legend overflow at 4k",
    context: "desktop › reports › fund",
    primaryValue: "18%",
    secondaryValue: "3/17 runs",
    barFraction: 0.18,
    barTone: "fail",
  },
  {
    primary: "Company profile — inline edit autosave race",
    context: "web › company › edit",
    primaryValue: "14%",
    secondaryValue: "2/14 runs",
    barFraction: 0.14,
    barTone: "fail",
  },
];

/**
 * This system tracks far more flaky tests than the 5 hand-authored ones
 * above — `TOTAL_ROW_COUNT` is the real, accurate total this page claims
 * (shown in the header badge), and `getRow(index)` below extends the
 * hardcoded `HIGHEST_FAILURE_RATE` array out to that count with
 * deterministic synthetic rows for infinite scroll, continuing the same
 * highest-failure-rate-first ordering.
 */
const TOTAL_ROW_COUNT = 55;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — copied from
 * `../recent-runs.tsx`/`../../workflows/pages/runs.tsx`, so the SAME index
 * always produces the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const AREA_POOL = [
  "web › auth",
  "web › company",
  "web › search",
  "desktop › pipeline",
  "desktop › portfolio",
  "desktop › reports",
  "desktop › company",
  "mobile › onboarding",
  "mobile › notifications",
  "mobile › portfolio",
];

const SUBJECT_POOL = [
  "Deal pipeline",
  "Portfolio dashboard",
  "Fund report",
  "Company profile",
  "Investor onboarding",
  "SSO login",
  "Notification center",
  "Search results",
  "Document viewer",
  "Cap table",
  "Data room",
  "Board pack export",
  "Comment thread",
  "Task assignment",
  "Meeting scheduler",
  "Contact import",
  "Watchlist",
  "Diligence checklist",
  "Signature request",
  "Audit log",
];

const SCENARIO_POOL = [
  "session refresh under load",
  "drag-and-drop reorder",
  "bulk edit across pages",
  "websocket reconnect after sleep",
  "concurrent autosave conflict",
  "large attachment upload",
  "keyboard-only navigation",
  "stale cache invalidation",
  "pagination boundary",
  "timezone-sensitive rendering",
  "offline-to-online sync",
  "role permission edge case",
  "virtualized list scroll jump",
  "modal focus trap",
  "deep-link restore",
  "multi-tab state drift",
  "retry-after-429 backoff",
  "expired token silent refresh",
  "long-poll timeout",
  "clipboard paste formatting",
];

const LAST_HARDCODED_FAILURE_RATE =
  HIGHEST_FAILURE_RATE[HIGHEST_FAILURE_RATE.length - 1].barFraction * 100;

/** A ranked row beyond the hand-authored `HIGHEST_FAILURE_RATE` sample —
 * deterministic per `index`. Fixed descending step per row minus jitter
 * strictly smaller than that step, so the failure rate stays strictly
 * decreasing no matter how the jitter rolls (never a LATER row showing a
 * HIGHER failure rate than an EARLIER one) — same technique as
 * `../../workflows/pages/active.tsx`'s `generateSyntheticActiveRun`. */
function generateSyntheticRow(index: number): RankedRow {
  const rand = mulberry32(hashSeed(`qa-highest-failure-rate-${index}`));

  const rowsBeyond = index - HIGHEST_FAILURE_RATE.length + 1;
  const stepPercent = 0.25;
  const jitterPercent = rand() * 0.2; // always smaller than the 0.25 step
  const ratePercent = Math.max(
    1,
    LAST_HARDCODED_FAILURE_RATE - rowsBeyond * stepPercent - jitterPercent,
  );
  const displayPercent = Math.max(1, Math.round(ratePercent));

  const subject = SUBJECT_POOL[Math.floor(rand() * SUBJECT_POOL.length)];
  const scenario = SCENARIO_POOL[Math.floor(rand() * SCENARIO_POOL.length)];
  const area = AREA_POOL[Math.floor(rand() * AREA_POOL.length)];

  const totalRuns = 8 + Math.floor(rand() * 25);
  const failRuns = Math.max(
    1,
    Math.min(totalRuns, Math.round((ratePercent / 100) * totalRuns)),
  );

  return {
    primary: `${subject} — ${scenario}`,
    context: area,
    primaryValue: `${displayPercent}%`,
    secondaryValue: `${failRuns}/${totalRuns} runs`,
    barFraction: ratePercent / 100,
    barTone: "fail",
  };
}

function getRow(index: number): RankedRow {
  return index < HIGHEST_FAILURE_RATE.length
    ? HIGHEST_FAILURE_RATE[index]
    : generateSyntheticRow(index);
}

/** Shimmering placeholder `<li>` shaped like a real ranked row — mirrors
 * `../recent-runs.tsx`'s `RankedRowSkeleton`, copied here since that file is
 * owned by a concurrent edit and not imported from. */
function RankedRowSkeleton({ index }: { index: number }) {
  return (
    <li className={styles.rankedRow} aria-hidden="true">
      <span className={styles.rankedIndex}>{index + 1}</span>
      <div className={styles.rankedMain}>
        <SkeletonBlock height={13} width={`${62 + ((index * 13) % 30)}%`} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={`${38 + ((index * 11) % 24)}%`} />
        </div>
        <div style={{ marginTop: 6 }}>
          <SkeletonBlock height={6} width="100%" radius="var(--radius-full)" />
        </div>
      </div>
      <div className={styles.rankedValues}>
        <SkeletonBlock height={13} width={40} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={52} />
        </div>
      </div>
    </li>
  );
}

/**
 * Full-page `/qa-automation/highest-failure-rate` route: the "maximised"
 * table view of the QA automation dashboard's "Tests with the highest
 * failure rate" ranked card, opened via a grow transition from that card
 * (shared `viewTransitionName`). The shell's breadcrumbs already read
 * "QA Automation > Tests with the highest failure rate" for this route, so
 * this page renders no title of its own — just the card, filling the outlet
 * and scrolling internally so the shell chrome stays fixed.
 */
export function QaHighestFailureRatePage() {
  useDocumentTitle("Tests with the highest failure rate");

  const navigate = useNavigate();
  const goToDashboard = () =>
    runViewTransition(() => navigate("/qa-automation"));

  // Initial page-mount loading state — distinct from `isLoadingMore` below,
  // which only covers the infinite-scroll "load more" case further down the
  // list. This one covers the very first render, before any real rows (not
  // even the first `initialCount` page) have ever appeared.
  const isInitialLoading = useSimulatedLoad();

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_ROW_COUNT,
    initialCount: HIGHEST_FAILURE_RATE.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });

  const loadedRows = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getRow(index)),
    [loadedCount],
  );

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={
          { viewTransitionName: "qa-card-highest-failure-rate" } as unknown as CSSProperties
        }
      >
        <header className={styles.header}>
          <span className={styles.headerTitle}>
            Tests with the highest failure rate
            <span className={styles.count}>{TOTAL_ROW_COUNT}</span>
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
          <div ref={tableWrapRef} className={styles.tableWrap}>
            <ol className={styles.rankedList}>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <RankedRowSkeleton key={`initial-skeleton-${index}`} index={index} />
                ))
              ) : (
                <>
                  {loadedRows.map((row, idx) => (
                    <li
                      key={`${row.primary}-${idx}`}
                      className={styles.rankedRow}
                      title={row.primary}
                    >
                      <span className={styles.rankedIndex}>{idx + 1}</span>
                      <div className={styles.rankedMain}>
                        <div className={styles.rankedPrimary}>{row.primary}</div>
                        {row.context && (
                          <div className={styles.rankedContext}>{row.context}</div>
                        )}
                        <div className={styles.rankedBarTrack}>
                          <div
                            className={`${styles.rankedBarFill} ${styles.rankedBarFail}`}
                            style={{
                              width: `${Math.round(
                                Math.max(0, Math.min(1, row.barFraction)) * 100,
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.rankedValues}>
                        <div className={styles.rankedPrimaryValue}>{row.primaryValue}</div>
                        <div className={styles.rankedSecondaryValue}>
                          {row.secondaryValue}
                        </div>
                      </div>
                    </li>
                  ))}
                  {hasMore && (
                    <li aria-hidden="true" className={styles.sentinelRow}>
                      <div ref={sentinelRef} />
                    </li>
                  )}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <RankedRowSkeleton key={`skeleton-${index}`} index={index} />
                    ))}
                </>
              )}
            </ol>
          </div>
          <ScrollShadow atTop={atTop} atBottom={atBottom} />
        </div>
      </section>
    </div>
  );
}
